import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Card,
  Col,
  ListGroup,
  ListGroupItem,
  Row,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { Anchor } from '../../components/Anchor';
import { SideBar } from '../../components/SideBar';
import { Spinner } from '../../components/Spinner';
import { Title } from '../../components/Typography';

import { getUser } from '../../selectors/user';

import { api } from '../../utils/api';

import classes from './orders.module.scss';

export const Orders: FunctionComponent = () => {
  const [fetching, setFetching] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const user = useSelector(getUser);

  const fetchOrders = useCallback(async () => {
    setFetching(true);

    try {
      const { data } = await api.get(`/user/${user.data!.id}/orders`, {
        Authorization: `Bearer ${user.data!.token}`,
      });

      setOrders(data);
    } finally {
      setFetching(false);
    }
  }, [user.data]);

  const getFinancialStatus = useCallback((status: string) => {
    const statuses: { [key: string]: string } = {
      pending: 'In attesa del pagamento',
      authorized: 'Il pagamento è stato autorizzato',
      partially_paid: 'Parzialmente pagato',
      paid: 'Pagato',
      partially_refunded: 'Parzialmente rimborsato',
      refunded: 'Rimborsato',
      voided: 'Il pagamento è stato rifiutato',
    };

    return statuses[status];
  }, []);

  const getShipmentStatus = useCallback((status: string) => {
    const statuses: { [key: string]: string } = {
      label_printed: 'Etichetta stampata',
      label_purchased: 'Etichetta acquistata',
      attempted_delivery: 'Tentata consegna',
      ready_for_pickup: 'Pronto per il ritiro',
      confirmed: 'Confermato',
      in_transit: 'In transito',
      out_for_delivery: 'In consegna',
      delivered: 'Consegnato',
      failure: 'Si è verificato un problema con la consegna',
    };

    return statuses[status];
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return (
    <SideBar>
      <Title as={'h3'}>I miei ordini</Title>

      <div>
        <Spinner show={fetching} />
      </div>

      <div>
        {orders.map((order) => (
          <Card className={classes.order} key={order.id}>
            <Card.Header>
              <Row>
                <Col xs={4} md={4} lg={2}>
                  <div>
                    <p className={'text-muted'}>Ordine effettuato</p>
                    <p>{new Date(order.created_at).toLocaleDateString()}</p>
                  </div>
                </Col>

                <Col xs={4} md={4} lg={2}>
                  <div>
                    <p className={'text-muted'}>Totale</p>
                    <p>
                      {order.subtotal_price} {order.currency}
                    </p>
                  </div>
                </Col>

                {order.fulfillments.map((fulfillment: any, index: number) => (
                  <Col key={index} xs={4} md={4} lg={2}>
                    <div>
                      <p className={'text-muted'}>Numero di tracking</p>
                      <p>{fulfillment.tracking_company}</p>
                      <Anchor href={fulfillment.tracking_url}>
                        {fulfillment.tracking_number}
                      </Anchor>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card.Header>
            <Card.Body>
              <div className={classes.badges}>
                <Badge variant={'success'}>
                  {getFinancialStatus(order.financial_status)}
                </Badge>

                {order.fulfillments.map((fulfillment: any, index: number) => (
                  <Badge variant={'success'} key={index}>
                    {getShipmentStatus(fulfillment.shipment_status)}
                  </Badge>
                ))}
              </div>

              <Card.Title>Indirizzo di spedizione:</Card.Title>
              <Card.Text>
                <p>
                  <strong>{order.shipping_address.name}</strong>
                </p>
                <p>{order.shipping_address.address1}</p>
                <p>
                  {order.shipping_address.city},{' '}
                  {order.shipping_address.province}
                </p>
                <p>{order.shipping_address.zip}</p>
                <p>{order.shipping_address.country}</p>
              </Card.Text>
            </Card.Body>
            <ListGroup className='list-group-flush'>
              {order.line_items.map((lineItem: any) => (
                <ListGroupItem className={classes.product} key={lineItem.id}>
                  <div>
                    <div>{lineItem.name}</div>

                    {order.refunds.some((refund: any) => refund.id === lineItem.id) && (
                      <Badge variant={'success'}>
                        Rimborsato
                      </Badge>
                    )}
                  </div>

                  <div>
                    {lineItem.price} {order.currency} x {lineItem.quantity}
                  </div>
                </ListGroupItem>
              ))}
            </ListGroup>
          </Card>
        ))}
      </div>
    </SideBar>
  );
};
